import { StationApi } from 'api/StationApi'
import { Button } from 'components/Button/Button'
import { ReactComponent as FirmwareUpdateSvg } from 'images/icons/firmware_update.svg'
import { useEffect, useState } from 'react'

import type { useStationControlBoard } from '../../StationControl/useStationControlBoard'
import style from '../StationDetails.module.css'

const REASON_LABELS: Record<string, string> = {
  unknown_version: 'Version unknown (update to release still allowed)',
  offline: 'Station offline',
  no_fresh_telemetry: 'No fresh telemetry',
  empty_lots: 'No lots in telemetry',
  occupied: 'Station occupied',
  no_available_version: 'No available release in catalog',
  already_up_to_date: 'Already up to date',
  update_disabled_by_flag: 'Remote update not enabled yet',
  trigger_not_configured: 'Update trigger not configured',
  update_in_progress: 'Software update in progress',
}

type BoardState = ReturnType<typeof useStationControlBoard>

type StationSoftwareSectionProps = {
  boardState: BoardState
  stationId: number
}

const StationSoftwareSection = ({ boardState, stationId }: StationSoftwareSectionProps) => {
  const { board, uiLocked, opsBusy, softwareUpdateInProgress, runOp } = boardState
  const status = board?.software ?? null
  const [, setTick] = useState(0)

  useEffect(() => {
    if (!status?.update_in_progress) return
    const timer = window.setInterval(() => setTick(t => t + 1), 1000)
    return () => window.clearInterval(timer)
  }, [status?.update_in_progress])

  if (!status) {
    return null
  }

  const current = status.current_version || '—'
  const available = status.available_version || '—'
  const showAvailable =
    Boolean(status.available_version) && status.current_version !== status.available_version
  /** Hub refresh failed but last good tags kept — Available may be outdated. */
  const catalogMayBeOutdated = Boolean(status.catalog_error && status.available_version)
  const catalogHoverHint = status.catalog_error
    ? catalogMayBeOutdated
      ? `Available version may be outdated. ${status.catalog_error}`
      : `Catalog refresh failed. ${status.catalog_error}`
    : undefined
  const updateDisabled =
    !status.update_allowed || uiLocked || Boolean(status.update_in_progress)

  let clientSpinner = Boolean(status.update_spinner)
  if (status.update_in_progress && status.pending_triggered_at && status.spinner_timeout_sec) {
    const started = Date.parse(status.pending_triggered_at)
    if (!Number.isNaN(started)) {
      clientSpinner = Date.now() - started < status.spinner_timeout_sec * 1000
    }
  }
  const showSpinner = opsBusy || clientSpinner

  const reasonLabels = (status.disabled_reasons || [])
    .map(reason => REASON_LABELS[reason] || reason)
    .filter(Boolean)
  const buttonTitle = softwareUpdateInProgress
    ? REASON_LABELS.update_in_progress
    : uiLocked
      ? 'Another station operation is in progress'
      : reasonLabels.length
        ? reasonLabels.join('; ')
        : 'Update unavailable'

  return (
    <div className={style.tableWrapper}>
      <div className={style.tableRow} style={{ gridTemplateColumns: '1fr 2fr 2fr 3fr' }}>
        <div>
          <FirmwareUpdateSvg />
        </div>
        <div className={style.textAlignLeft}>Software</div>
        <div className={style.centered} title={buttonTitle}>
          <Button.Standart
            className={style.controlButton}
            disabled={updateDisabled}
            loading={showSpinner}
            onClick={() => {
              if (!status.update_allowed || uiLocked) return
              void runOp(() => StationApi.requestSoftwareUpdate(stationId), {
                patchSoftware: prev => ({
                  ...prev,
                  update_allowed: false,
                  update_in_progress: true,
                  update_spinner: true,
                  pending_triggered_at: new Date().toISOString(),
                  spinner_timeout_sec: prev.spinner_timeout_sec ?? 180,
                  disabled_reasons: Array.from(
                    new Set([...(prev.disabled_reasons || []), 'update_in_progress'])
                  ),
                }),
              })
            }}
          >
            Update
          </Button.Standart>
        </div>
        <div className={style.percents} title={catalogHoverHint || buttonTitle}>
          {showAvailable || catalogMayBeOutdated ? (
            <>
              Current: {current}
              <br />
              <span title={catalogHoverHint || buttonTitle}>Available: {available}</span>
            </>
          ) : (
            <span title={catalogHoverHint || buttonTitle}>Current: {current}</span>
          )}
        </div>
      </div>
    </div>
  )
}

export default StationSoftwareSection
